import * as z from 'zod/mini'
import { refineWithKey, ValidationKey } from '@seiri/shared/common'

/**
 * A required number input in a Signal Form. Signal Forms reads an empty number input as `null`, so
 * the form model holds `number | null` while the HTTP body wants a `number`: the field accepts `null`
 * in its input type (so the model is a valid schema input) and reports it as missing. The mapper to
 * the body can then rely on the value being set. See rules 3 and 4 in docs/validation.md.
 */
export const requiredFormNumber = <T extends z.ZodMiniType<number, number>>(schema: T) =>
  z.nullable(schema).check(refineWithKey('validation.required', (value: number | null) => value !== null))

/**
 * For a mapper whose form already validated the value: turns the `null` a number input can hold into
 * an error instead of silently sending `0`.
 */
export const assertFormNumber = (value: number | null, field: string): number => {
  if (value === null) {
    throw new Error(`${field} is empty; the form should not have submitted`)
  }
  return value
}

/** A text the form requires even where the API accepts an empty string (a name on a profile). */
export const requiredText = refineWithKey('validation.required', (value: string) => value.trim().length > 0)

/** A required date picker: the model holds `Date | null`, `null` is reported as missing. */
export const requiredFormDate = () =>
  z.nullable(z.date()).check(refineWithKey('validation.required', (value: Date | null) => value !== null))

/** A list the form requires at least one entry of (weekdays, intervals, places), failing with `key`. */
export const nonEmpty = (key: ValidationKey) => refineWithKey(key, (value: readonly unknown[]) => value.length > 0)
